import type {
  ApiResponse,
  BulkInviteWishlistRequest,
  BulkInviteWishlistResponse,
} from '@fluenceur/types';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toaster } from '@/components/ui/toaster';
import api from '@/libs/axiosInstance';
import i18next from 'i18next';
import { isPlanLimitError } from '@/libs/plan-limit-refusal';

type Input = BulkInviteWishlistRequest;
type Response = BulkInviteWishlistResponse;

interface ApiErrorResponse {
  success: false;
  message: string;
  code?: string;
}

/**
 * Get translated error message from error code or fallback to provided message
 */
function getTranslatedErrorMessage(code?: string, fallbackMessage?: string): string {
  if (code) {
    // Check for rate limit error (429)
    if (code === 'E_TOO_MANY_REQUESTS') {
      return i18next.t('errors.E_TOO_MANY_REQUESTS');
    }
    // Try to get translation for the error code
    const translationKey = `errors.${code}`;
    const translated = i18next.t(translationKey);
    // If translation exists (not the same as the key), use it
    if (translated !== translationKey) {
      return translated;
    }
  }
  return fallbackMessage || i18next.t('invitations.modal.bulk.errorDefault');
}

async function bulkInviteWishlistFn(params: Input): Promise<Response> {
  try {
    const response = await api.post<ApiResponse<Response>>(
      '/dashboard/wishlist/bulk-invite',
      params
    );

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    // Handle API error response (success: false)
    const errorCode = (response.data as unknown as ApiErrorResponse).code;
    const errorMessage = getTranslatedErrorMessage(errorCode, response.data.message);
    throw new Error(errorMessage);
  } catch (error) {
    // A plan refusal keeps its response: the upsell answers it, not a message.
    if (isPlanLimitError(error)) throw error;
    // Handle Axios errors (network errors, 4xx, 5xx responses)
    if (error instanceof AxiosError && error.response?.data) {
      const errorData = error.response.data as ApiErrorResponse;
      // Handle rate limiting (429)
      if (error.response.status === 429) {
        throw new Error(getTranslatedErrorMessage('E_TOO_MANY_REQUESTS'));
      }
      const errorMessage = getTranslatedErrorMessage(errorData.code, errorData.message);
      throw new Error(errorMessage);
    }
    // Re-throw if it's already a processed Error
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(i18next.t('invitations.modal.bulk.errorDefault'));
  }
}

export function useBulkWishlistInvite() {
  return useMutation<Response, Error, Input>({
    mutationFn: bulkInviteWishlistFn,
    // Note: Cache invalidation is handled by the Pusher onComplete callback
    // in invitation-details-view.tsx to ensure it happens after all invitations complete
    onError: (error) => {
      // A plan refusal opens the upsell from the API client; a toast would repeat it.
      if (isPlanLimitError(error)) return;
      toaster.error({
        title: i18next.t('invitations.modal.bulk.errorTitle'),
        description: error.message,
      });
    },
  });
}
